var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import { trackEvent } from '@raisin/events-tracking';
import { refreshJSessionId } from '../api/services';
import { MOBILE_WEBVIEW_IDENTIFIER, NEW_LOGIN_ISSUANCE_BUFFER, SESSION_EXPIRATION_COUNTDOWN_TIME, } from '../constants';
import { EventAction } from '../enums/events';
import { createSessionTrackEventProps } from '../eventsTracking/utils';
import { getAuthTimeDiff, getTokenExpirationTime, getTokenIssuanceTime, getUnixTime, isOnMobileApp, setLoginReferer, } from '../utils';
import { logOutTheCustomer, refreshJWT } from '../utils/session-refresher';
export const useCountdownNew = ({ platform, disabled = false, flags, }) => {
    var _a, _b, _c, _d;
    const [tokenTimes, setTokenTimes] = useState({ timeDiff: 0, expiration: 0, issuance: 0, session: 0 });
    const countdownInterval = useRef();
    const [isTokenRefreshing, setIsTokenRefreshing] = useState(false);
    const [countdown, setCountdown] = useState('--:--');
    const [isSessionCountdownMsgOpened, setIsSessionCountdownMsgOpened] = useState(false);
    const [isSessionExtendedMsgOpened, setIsSessionExtendedMsgOpened] = useState(false);
    const [isSessionExtendable, setIsSessionExtendable] = useState(true);
    const [lastActiveTime, setLastActiveTime] = useState(() => getUnixTime());
    const userManagementCallObsLogout = (_a = flags === null || flags === void 0 ? void 0 : flags.userManagementCallObsLogout) !== null && _a !== void 0 ? _a : false;
    const umtCallOBSLoginEndpointFromFE = (_b = flags === null || flags === void 0 ? void 0 : flags.umtCallOBSLoginEndpointFromFE) !== null && _b !== void 0 ? _b : false;
    const umtFECallKeycloakLogoutOnSessionTimeout = (_c = flags === null || flags === void 0 ? void 0 : flags.umtFECallKeycloakLogoutOnSessionTimeout) !== null && _c !== void 0 ? _c : false;
    const umtStopRefreshingTokenWhenAboutToReachMaxSession = (_d = flags === null || flags === void 0 ? void 0 : flags.umtStopRefreshingTokenWhenAboutToReachMaxSession) !== null && _d !== void 0 ? _d : false;
    const updateTokenTimes = useCallback(() => {
        const timeDiff = getAuthTimeDiff();
        const issuance = getTokenIssuanceTime();
        const expiration = getTokenExpirationTime();
        const session = expiration - issuance;
        const times = { timeDiff, expiration, issuance, session };
        setTokenTimes(times);
        return times;
    }, []);
    const initialize = useCallback(() => {
        const { issuance, timeDiff } = updateTokenTimes();
        if (getUnixTime(-timeDiff) - issuance < NEW_LOGIN_ISSUANCE_BUFFER) {
            setLastActiveTime(issuance + timeDiff);
        }
        else {
            setLastActiveTime(getUnixTime());
        }
    }, [setLastActiveTime, updateTokenTimes]);
    const trackSessionEvent = useCallback((action, props = {}) => trackEvent(createSessionTrackEventProps(action, platform, props), true), [platform]);
    const updateCountdown = useCallback((count) => {
        const minutesRaw = Math.floor(count / 60);
        const secondsRaw = count - minutesRaw * 60;
        const minutes = `0${minutesRaw}`.slice(-2);
        const seconds = `0${secondsRaw}`.slice(-2);
        setCountdown(`${minutes}:${seconds}`);
    }, [setCountdown]);
    const handleSessionCountdownMsgClose = useCallback(() => {
        setIsSessionCountdownMsgOpened(false);
        console.info('Countdown notification hidden!');
    }, []);
    const handleSessionExtendedMsgClose = useCallback(() => setIsSessionExtendedMsgOpened(false), []);
    const refreshToken = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setIsTokenRefreshing(true);
            yield refreshJWT({
                userManagementCallObsLogout,
                umtFECallKeycloakLogoutOnSessionTimeout,
                platform,
            });
            if (umtCallOBSLoginEndpointFromFE) {
                yield refreshJSessionId({
                    userManagementCallObsLogout,
                    umtFECallKeycloakLogoutOnSessionTimeout,
                    platform,
                });
            }
            // Close the countdown notification
            handleSessionCountdownMsgClose();
            // Open the session extension notification only if the countdown notification was opened
            if (isSessionCountdownMsgOpened) {
                setIsSessionExtendedMsgOpened(true);
            }
        }
        catch (_e) {
            // Hide session countdown notification:
            handleSessionCountdownMsgClose();
        }
        finally {
            setIsTokenRefreshing(false);
            updateTokenTimes();
        }
    }), [
        handleSessionCountdownMsgClose,
        isSessionCountdownMsgOpened,
        platform,
        umtFECallKeycloakLogoutOnSessionTimeout,
        updateTokenTimes,
        userManagementCallObsLogout,
        umtCallOBSLoginEndpointFromFE,
    ]);
    const handleSessionExtendClick = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield refreshToken();
            console.info('Token has been extended manually!');
            trackSessionEvent(EventAction.SESSION_EXTENDED_MANUALLY);
        }
        catch (error) {
            console.error(error);
        }
    }), [refreshToken, trackSessionEvent]);
    const handleTimeout = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        console.info('Session has been expired!');
        trackSessionEvent(EventAction.SESSION_EXPIRED);
        handleSessionCountdownMsgClose();
        clearInterval(countdownInterval.current);
        setLoginReferer();
        yield logOutTheCustomer({
            userManagementCallObsLogout,
            umtFECallKeycloakLogoutOnSessionTimeout,
            platform,
        });
    }), [
        handleSessionCountdownMsgClose,
        platform,
        trackSessionEvent,
        umtFECallKeycloakLogoutOnSessionTimeout,
        userManagementCallObsLogout,
    ]);
    const shouldShowCountDownMsg = (isIdleSessionAboutToExpire, isIdleSessionValidationDisabled) => isIdleSessionAboutToExpire && !isIdleSessionValidationDisabled;
    /**
     * Session should expire when:
     * - User is idle for session lifetime
     * - Token expiration time is reached
     *
     * Session should extend when:
     * - User do an activity (click, type, navigate)
     * - User manually extend his session from the session-timeout notification
     */
    const monitorSession = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const isIdleSessionValidationDisabled = isOnMobileApp();
        const { timeDiff, expiration, session, issuance } = tokenTimes;
        if (session <= SESSION_EXPIRATION_COUNTDOWN_TIME &&
            umtStopRefreshingTokenWhenAboutToReachMaxSession) {
            setIsSessionExtendable(false);
        }
        const idleDuration = Math.max(Math.round(getUnixTime() - lastActiveTime), 0);
        const idleDurationRemainingToExpire = Math.max(session - idleDuration, 0);
        const tokenDurationRemainingToExpire = Math.max(Math.round(expiration - getUnixTime(-timeDiff)), 0);
        const expiresIn = isIdleSessionValidationDisabled
            ? tokenDurationRemainingToExpire
            : Math.max(Math.min(idleDurationRemainingToExpire, tokenDurationRemainingToExpire), 0);
        const isIdleSessionAboutToExpire = idleDurationRemainingToExpire <= SESSION_EXPIRATION_COUNTDOWN_TIME;
        const isTokenSessionAboutToExpire = tokenDurationRemainingToExpire <= SESSION_EXPIRATION_COUNTDOWN_TIME;
        const isSessionExpired = expiresIn <= 0;
        const debugMessage = `IdleFor: ${idleDuration} | IdleExpiresIn: ${idleDurationRemainingToExpire} | TokenIssuedFor: ${Math.round(getUnixTime(-timeDiff) - issuance)} | TokenExpiresIn: ${tokenDurationRemainingToExpire} | ExpiresIn: ${expiresIn}`;
        console.debug(debugMessage);
        if (isTokenRefreshing)
            return;
        // Logout immediately if the session expired
        if (isSessionExpired) {
            yield handleTimeout();
            return;
        }
        updateCountdown(expiresIn);
        // Show countdown notification if idle session is about to expire
        if (shouldShowCountDownMsg(isIdleSessionAboutToExpire, isIdleSessionValidationDisabled)) {
            if (!isSessionCountdownMsgOpened) {
                setIsSessionCountdownMsgOpened(true);
                console.info('Countdown notification shown!');
                trackSessionEvent(EventAction.SESSION_COUNTDOWN_SHOWN);
                return;
            }
        }
        // Refresh token automatically if token session is about to expire and idle session is still active
        if (isTokenSessionAboutToExpire && isSessionExtendable) {
            try {
                yield refreshToken();
                console.info('Token has been extended automatically!');
                trackSessionEvent(EventAction.SESSION_EXTENDED_AUTOMATICALLY, {
                    customAttribute1: tokenDurationRemainingToExpire.toString(),
                    customAttribute2: isIdleSessionValidationDisabled ? MOBILE_WEBVIEW_IDENTIFIER : undefined,
                });
            }
            catch (error) {
                console.error(error);
            }
        }
    }), [
        isSessionCountdownMsgOpened,
        handleTimeout,
        isTokenRefreshing,
        isSessionExtendable,
        lastActiveTime,
        refreshToken,
        tokenTimes,
        trackSessionEvent,
        umtStopRefreshingTokenWhenAboutToReachMaxSession,
        updateCountdown,
    ]);
    useEffect(() => {
        if (disabled)
            return;
        countdownInterval.current = setInterval(monitorSession, 1000);
        return () => clearInterval(countdownInterval.current);
    }, [disabled, monitorSession]);
    useEffect(() => {
        if (disabled)
            return;
        initialize();
        const events = ['mouseup', 'keydown', 'touchend'];
        // Updates "lastActiveTime" on a one second throttled interactions/activities
        const handleEvent = throttle(() => {
            setLastActiveTime(getUnixTime());
        }, 1000);
        events.forEach(function (e) {
            document.addEventListener(e, handleEvent);
        });
        return () => {
            events.forEach(function (e) {
                document.removeEventListener(e, handleEvent);
            });
        };
    }, [disabled, initialize, setLastActiveTime]);
    return {
        countdown,
        isSessionCountdownMsgOpened,
        isSessionExtendedMsgOpened,
        isSessionExtendable,
        handleSessionExtendClick,
        handleSessionCountdownMsgClose,
        handleSessionExtendedMsgClose,
    };
};
//# sourceMappingURL=useCountdownNew.js.map